import type { Block } from "building-types/blocks/blockTypes";
import type { Building } from "building-types/building/buildingTypes";
import { StreetOrientationMode } from "building-types/building/streetOrientationMode";
import { FoundationDatumVerticalAnchor } from "building-types/datums/datumsTypesEnum";
import type { GeneratedDoorPlacement } from "building-types/doors/doorTypes";
import type { Facade, WallFacadeTable } from "building-types/facades/facadeTypes";
import { FacadeTrimBehavior } from "building-types/facades/facadeTypesEnum";
import type { Label } from "building-types/facades/label";
import type { HalfEdgeToFloorPath } from "building-types/floors/floorSystemTypes";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import { roomEntityHandleForSelectingReportingUnit } from "building-types/handles/roomEntityHandleForSelectingReportingUnit";
import type { Level } from "building-types/levels/levelTypes";
import type {
  HalfEdgeAssociationTable,
  PlanarGraphLevelAssociations,
} from "building-types/planarGraphs/planarGraphAssociationTypes";
import type { PlanarGraphPerimeterInfo } from "building-types/planarGraphs/planarGraphPerimeterInfo";
import type { Room } from "building-types/rooms/roomTypes";
import { RoomEnclosure } from "building-types/rooms/roomTypesEnum";
import type { DoorSettings } from "building-types/settings/doorSettings";
import type { FoundationSettings } from "building-types/settings/foundationSettings";
import { WainscotingCapType } from "building-types/settings/settingsTypesEnum";
import { WainscotingViewNoteType } from "building-types/settings/viewNotesSettings";
import type { WainscotingSettings } from "building-types/settings/wainscotingSettings";
import type { WallSettings } from "building-types/settings/wallSettings";
import { ViewEntityType } from "building-types/views/viewEntityType";
import type { ViewEntityDescriptor } from "building-types/views/viewEntityTypes";
import type { WainscotingWallSegments } from "building-types/wainscoting/wainscotingTypes";
import {
  wainscotingWallSegmentsNew,
  type FrontInfo,
  type GenerateWainscotingContext,
  type Wainscoting,
  type WallWainscotingFaces,
} from "building-types/wainscoting/wainscotingTypes";
import { PathPriority } from "building-types/walls/pathPriority";
import { WallType } from "building-types/walls/wallEnums";
import { WallLayerType } from "building-types/walls/wallEnums";
import type { WallGap } from "building-types/walls/wallGap";
import { WallGapSource } from "building-types/walls/wallGap";
import { type WallSystem } from "building-types/walls/wallSytemTypes";
import type { SolidWall, SolidWallMass, SolidWallSide, Wall } from "building-types/walls/wallTypes";
import type { RWTable, Table } from "common/data/table";
import { assertIsNotNullOrUndefined, assertIsNotUndefined } from "common/utils/assertOfType";
import { assertUnreachable } from "common/utils/assertUnreachable";
import { levelInchesNew } from "common/utils/nominalNumbers";
import objectFind from "common/utils/objectFind";
import uid from "common/utils/uid";
import { floorAssemblyBaseOffset } from "doc/elements/floor/floorAssembly";
import { foundationSystemSettingsFromSettingsConfig } from "doc/elements/foundation/foundationSettingsTools";
import { isExteriorRoomEnclosure, isWalledRoomEnclosure } from "doc/rooms/roomAttributesTools";
import { buildingEntityBaseNew } from "doc/utils/buildingEntityTools";
import { viewEntityDescriptorNew } from "doc/utils/viewEntityTools";
import type { BRep, Face } from "geom/brep";
import { faceOrNullMove } from "geom/brep";
import { intersectionLinePlaneBSP, vec3PlaneBSPSide } from "geom/bsp";
import type { PlaneBSP } from "geom/bspTypes";
import { DIST_EPSILON, DIST_EPSILON_SQUARED } from "geom/core";
import { extrudeFaceAlongPolyLine3, extrudeLine2 } from "geom/extrude";
import { joinPolyLine2Sequences } from "geom/join";
import { offsetPolyLine2 } from "geom/offset";
import type { PlanarGraph, PlanarGraphLoops } from "geom/planarGraph";
import {
  planarGraphHalfEdgeEnd,
  planarGraphHalfEdgeLength,
  planarGraphHalfEdgeNormal,
  planarGraphHalfEdgeStart,
  planarGraphHalfEdgeTangent,
  planarGraphTraceFaceGroupPerimeter,
  planarGraphVertexStripToPolyLine2,
} from "geom/planarGraph";
import { PlaneSide, planeMove, planeNewXY, planeNormal } from "geom/plane";
import { planeBSPByPointAndNormal, planeBSPMoveAlongNormal } from "geom/planeBSP";
import {
  polyLine2ByTwoPoints,
  polyLine2Concat,
  polyLine2Copy,
  polyLine2Edge,
  polyLine2EdgeCount,
  polyLine2EdgeEval,
  polyLine2EdgeLength,
  polyLine2EdgeNormal,
  polyLine2ExtendEnd,
  polyLine2ExtendStart,
  polyLine2IsClosed,
  polyLine2Length,
  polyLine2MergeCoincidentVertices,
  polyLine2MergeCollinearEdges,
  polyLine2Move,
  polyLine2Reverse,
} from "geom/polyLine2";
import { polyLine2New, type PolyLine2 } from "geom/polyLine2Types";
import { polyLine3ExtendEnd, polyLine3ExtendStart, polyLine3OnPlane } from "geom/polyLine3";
import { vec2DistSquared, vec2Dot } from "geom/vec2";
import { iterateFacadesForHalfEdge } from "../utils/facade";
import { getDoorRoughOpeningAdjustment, isGarageDoorOperationType } from "./doors/doorUtils";
import { slabDepthAtRoomClass } from "./foundation/slabUtils";
import { bottomLevelId, groundLevelId } from "./level/levelTools";
import { getFurthestEdgeIndex } from "./streetOrientation";
import { getWallGaps, solidWallCrossSectionEdges } from "./walls/solidWallCrossSectionUtils";
import { getWallPath } from "./walls/wall";
import { solidWallLayerWithFallback, solidWallSubstrateLayer, wallMassCrossSectionLayer } from "./walls/wallLayers";
import { wallSideNew } from "./walls/wallSideBasics";
import { makeWallMassSystemCustom } from "./walls/wallSystem";

const DEFAULT_WAINSCOTING_SIDE_THRESHOLD = 5000;
const DOOR_BOTTOM_WAINSCOTING_THRESHOLD = 10;
const WINDOW_BOTTOM_WAINSCOTING_THRESHOLD = 10;

function frontInfoNew(
  edgeAtFront: boolean[] = [],
  leftClipPlane: PlaneBSP | null = null,
  rightClipPlane: PlaneBSP | null = null,
): FrontInfo {
  return {
    edgeAtFront,
    leftClipPlane,
    rightClipPlane,
  };
}

enum WainscotingCorner {
  NONE,
  CONVEX,
  CONCAVE,
}

export function generateWainscotingContextNew(
  wainscoting: RWTable<Wainscoting> = {},
  leftClipPlane?: PlaneBSP,
  rightClipPlane?: PlaneBSP,
): GenerateWainscotingContext {
  return {
    wainscoting,
    leftClipPlane,
    rightClipPlane,
  };
}

function wainscotingBehaviorForEdge(
  wallFacadeTable: WallFacadeTable,
  labels: HalfEdgeAssociationTable<Label>,
  edge: number,
): FacadeTrimBehavior | null {
  let result: FacadeTrimBehavior | null = null;

  iterateFacadesForHalfEdge(wallFacadeTable, labels, edge, (facade) => {
    if (facade && facade.wainscotingBehavior) {
      result = facade.wainscotingBehavior;
      return true; // early return from the iteration
    }
    return false;
  });

  return result;
}

interface MakeHouseWainscotingOptions {
  level: Level;
  levels: Table<Level>;
  blocks: Table<Block>;
  walls: Table<WallSystem>;
  planarGraphInfo: Table<PlanarGraphPerimeterInfo>;
  planarGraphLevelAssociations: Table<PlanarGraphLevelAssociations>;
  planarGraph: PlanarGraph;
  wainscotingSettings: WainscotingSettings;
  doorSettings: DoorSettings;
  labels: HalfEdgeAssociationTable<Label>;
  mirror: boolean;
  facades: Table<Facade>;
  rooms: Table<Room>;
  isLastLevel: boolean;
  wainscotingContext: GenerateWainscotingContext;
}

export function makeHouseWainscoting({
  level,
  levels,
  blocks,
  walls,
  planarGraphInfo,
  planarGraphLevelAssociations,
  planarGraph,
  wainscotingSettings,
  doorSettings,
  labels,
  mirror,
  facades,
  rooms,
  isLastLevel,
  wainscotingContext,
}: MakeHouseWainscotingOptions) {
  const { floorElevation, topOfWall, levelDatumsGenerated } = level;
  const { height: wainscotingHeight, capType } = wainscotingSettings;
  const startHeightOffset = levelDatumsGenerated.wainscot;
  const coveredPerimeterLoops = planarGraphInfo[level.id].coveredPerimeters;

  const isFloorToCeiling = wainscotingHeight <= floorElevation + topOfWall;

  const addTopTrim = (isFloorToCeiling || isLastLevel) && capType !== WainscotingCapType.NONE;
  const addTopFace = level.floorIndex >= 0;
  const availableHeight = Math.min(wainscotingHeight - floorElevation, topOfWall);

  const wallSystem = walls[level.id];

  const isBasement = level.floorIndex < 0;

  if (availableHeight <= DIST_EPSILON) {
    return;
  }

  const allowWainscotingForEdge = (halfEdge: number): boolean => {
    const wainscotingBehavior = wainscotingBehaviorForEdge(facades, labels, halfEdge);

    if (wainscotingBehavior === FacadeTrimBehavior.ADD) {
      return true;
    } else if (wainscotingBehavior === FacadeTrimBehavior.REMOVE) {
      return false;
    }

    const wallId = wallSystem.halfEdgeToWall[halfEdge];
    const wall = wallSystem.walls[wallId];
    if (wall === undefined || (wall.wallType !== WallType.SOLID && !isBasement)) {
      return false;
    }

    const wallPath = getWallPath(wall);
    const { leftRoom, rightRoom } = wallPath;

    return (
      allowWainscotingForRoom(blocks, leftRoom ? rooms[leftRoom.id] : undefined) &&
      allowWainscotingForRoom(blocks, rightRoom ? rooms[rightRoom.id] : undefined)
    );
  };

  const getWallsAbove = ({
    wall,
    walls,
    levels,
    planarGraphLevelAssociations,
  }: {
    wall: Wall;
    walls: Table<WallSystem>;
    levels: Table<Level>;
    planarGraphLevelAssociations: Table<PlanarGraphLevelAssociations>;
  }): Wall[] => {
    const wallPath = getWallPath(wall);
    const wallLevelId = wallPath.leftRoom?.level.id ?? wallPath.rightRoom?.level.id;
    if (!wallLevelId) {
      return [];
    }

    const wallLevel = levels[wallLevelId];
    const wallHeAbove = planarGraphLevelAssociations[wallLevelId].halfEdgesAbove[wallPath.halfEdgeIndex];

    if (!wallHeAbove?.length) {
      return [];
    }

    if (!wallLevel.levelAbove) {
      return [];
    }

    const wallSystemAbove = walls[wallLevel.levelAbove];
    const wallsAbove = wallHeAbove
      .map((he) => wallSystemAbove.walls[wallSystemAbove.halfEdgeToWall[he]])
      .filter((wall) => wall !== undefined);

    return wallsAbove;
  };

  const makeWainscotingForEdge: WainscotingEdgeGenerator = (
    { halfEdgeIndex, startCorner, endCorner, prevAtFront, currAtFront, nextAtFront, currentU },
    out,
  ) => {
    const { faces, trims, wallCuts, wallBases } = out;

    let amt = 0;
    let usedCloseDist = false;

    const wallId = wallSystem.halfEdgeToWall[halfEdgeIndex];
    const wall = wallSystem.walls[wallId];

    let edgeStartHeightOffset = startHeightOffset;
    if (
      groundLevelId(levels) === level.id &&
      level.levelBelow &&
      planarGraphLevelAssociations[level.id].halfEdgesBelow[halfEdgeIndex]?.length
    ) {
      // there is a basement on this level that will compensate the remaining wainscoting
      edgeStartHeightOffset = levelInchesNew(-level.floorPlateThickness);
    }

    if (isBasement) {
      // this is a basement, so we need to ensure we only draw edges that are allowed on the ground floor
      const wallsAbove = getWallsAbove({
        wall,
        walls,
        levels,
        planarGraphLevelAssociations,
      });
      if (!wallsAbove.length) {
        return [amt, usedCloseDist];
      }
      const wainscotingAbove = objectFind(wainscotingContext.wainscoting, (w) => w.levelId === level.levelAbove);
      for (const wallAbove of wallsAbove) {
        if (wallAbove.wallType === WallType.SOLID && wainscotingAbove?.faceInfo[wallAbove.id]) {
          // add wainscoting per sub edge of the ground floor
          const [subAmt, subUsedCloseDist] = addBasementWallWainscoting({
            wainscotingSettings,
            doorSettings,
            wainscotingFaces: faces,
            wainscotingTrim: trims,
            wallSystem,
            wall,
            wallAbove,
            startCorner,
            endCorner,
            prevAtFront,
            currAtFront,
            nextAtFront,
            currentU, // This is probably not fully accurate and will produce texture artifacts
            startHeightOffset: edgeStartHeightOffset,
            availableHeight,
            addTopTrim,
            wainscotingContext,
            wallCuts,
            wallBases,
            addTopFace,
          });

          amt += subAmt;
          usedCloseDist = subUsedCloseDist;
        }
      }
    } else {
      if (wall.wallType !== WallType.SOLID) {
        return [amt, usedCloseDist];
      }

      // add wainscoting
      [amt, usedCloseDist] = addSolidWallWainscoting({
        wainscotingSettings,
        doorSettings,
        wainscotingFaces: faces,
        wainscotingTrim: trims,
        wallSystem,
        wall,
        startCorner,
        endCorner,
        prevAtFront,
        currAtFront,
        nextAtFront,
        currentU,
        startHeightOffset: edgeStartHeightOffset,
        availableHeight,
        addTopTrim,
        wainscotingContext,
        wallBases,
        wallCuts,
        addTopFace,
      });
    }

    return [amt, usedCloseDist];
  };

  makeWainscoting({
    level,
    levels,
    perimeters: coveredPerimeterLoops,
    planarGraph,
    wainscotingSettings,
    wallSystem,
    facades,
    labels,
    mirror,
    wainscotingContext,
    allowWainscotingForEdge,
    makeWainscotingForEdge,
  });
}

interface MakeExteriorWainscotingOptions {
  level: Level;
  levels: Table<Level>;
  blocks: Table<Block>;
  coveredPerimeterFaces: number[];
  uncoveredPerimeterFaces: number[];
  planarGraph: PlanarGraph;
  wainscotingSettings: WainscotingSettings;
  wallSettings: WallSettings;
  foundationSettings: FoundationSettings;
  labels: HalfEdgeAssociationTable<Label>;
  wallSystem: WallSystem;
  floorPaths: HalfEdgeToFloorPath;
  mirror: boolean;
  facades: Table<Facade>;
  rooms: Table<Room>;
  isLastLevel: boolean;
  wainscotingContext: GenerateWainscotingContext;
}

export function makeExteriorWainscoting({
  level,
  levels,
  blocks,
  wallSystem,
  floorPaths,
  coveredPerimeterFaces,
  uncoveredPerimeterFaces,
  planarGraph,
  wainscotingSettings,
  wallSettings,
  foundationSettings,
  labels,
  mirror,
  facades,
  rooms,
  wainscotingContext,
}: MakeExteriorWainscotingOptions) {
  const { floorElevation, levelDatumsGenerated } = level;

  const addTopTrim = wainscotingSettings.capType === WainscotingCapType.CAP;
  const addTopFace = !addTopTrim;

  const startHeight = floorElevation + levelDatumsGenerated.wainscot;
  const startHeightOffset = 0;
  const availableHeight = Math.abs(levelDatumsGenerated.wainscot);

  if (availableHeight <= DIST_EPSILON) {
    return;
  }

  const foundationSystemSettings = foundationSystemSettingsFromSettingsConfig(foundationSettings);

  // detect exterior edges on covered rooms - can be expanded to other types later if needed
  const exteriorPerimeterFaces = coveredPerimeterFaces.filter((i) => !uncoveredPerimeterFaces.includes(i));
  const exteriorPerimeters = planarGraphTraceFaceGroupPerimeter(planarGraph, exteriorPerimeterFaces);
  const exterioWallPredicate = (wall: Wall) => {
    if (wall.wallType !== WallType.VOID) {
      return false;
    }

    const lEnclosure = wall.path.leftRoom?.attributes.roomEnclosure;
    const rEnclosure = wall.path.rightRoom?.attributes.roomEnclosure;

    if (!lEnclosure || !isWalledRoomEnclosure[lEnclosure] || !rEnclosure || !isWalledRoomEnclosure[rEnclosure]) {
      return true;
    }

    return false;
  };

  const exteriorWallOffset = (wall: Wall) => {
    const { halfEdgeIndex } = getWallPath(wall);
    const floorPath = floorPaths[halfEdgeIndex];
    const { floorOffset } = floorPath;
    return {
      rightOffset: floorOffset,
      leftOffset: -floorOffset,
    };
  };

  // we need a new wall mass system with SOLID/VOID walls inverted
  const exteriorWallSystem = makeWallMassSystemCustom({
    planarGraph,
    wallSystem,
    wallSettings,
    wallPredicate: exterioWallPredicate,
    wallOffset: exteriorWallOffset,
  });

  const allowWainscotingForEdge = (halfEdge: number): boolean => {
    const wainscotingBehavior = wainscotingBehaviorForEdge(facades, labels, halfEdge);

    if (wainscotingBehavior === FacadeTrimBehavior.ADD) {
      return true;
    } else if (wainscotingBehavior === FacadeTrimBehavior.REMOVE) {
      return false;
    }

    const wallId = wallSystem.halfEdgeToWall[halfEdge];
    const wall = wallSystem.walls[wallId];
    if (wall === undefined) {
      return false;
    }

    const { leftRoom, rightRoom } = getWallPath(wall);

    return (
      allowWainscotingForRoom(blocks, leftRoom ? rooms[leftRoom.id] : undefined) &&
      allowWainscotingForRoom(blocks, rightRoom ? rooms[rightRoom.id] : undefined)
    );
  };

  const wainscotingPathPriority = (leftRoom?: Room, rightRoom?: Room): PathPriority => {
    if (!leftRoom && !rightRoom) {
      return PathPriority.NONE;
    }

    if (!rightRoom) {
      return PathPriority.LEFT;
    }

    if (!leftRoom) {
      return PathPriority.RIGHT;
    }

    const lEnclosure = leftRoom.attributes.roomEnclosure;
    const rEnclosure = rightRoom.attributes.roomEnclosure;

    if (lEnclosure === rEnclosure) {
      return PathPriority.NONE;
    }

    if (lEnclosure === RoomEnclosure.OPEN) {
      return PathPriority.LEFT;
    }

    if (rEnclosure === RoomEnclosure.OPEN) {
      return PathPriority.RIGHT;
    }

    return PathPriority.NONE;
  };

  const makeWainscotingForEdge: WainscotingEdgeGenerator = (
    { halfEdgeIndex: edgeHalfEdge, prevAtFront, currAtFront, nextAtFront, startCorner, endCorner, currentU },
    out,
  ) => {
    const { faces, trims, wallCuts, wallBases } = out;

    let amt = 0;
    let usedCloseDist = false;

    const wallId = exteriorWallSystem.halfEdgeToWall[edgeHalfEdge];
    const wall = exteriorWallSystem.walls[wallId];

    if (wall.wallType === WallType.SOLID_MASS) {
      const { leftEdge, rightEdge, path } = wall;
      const { leftRoom, rightRoom, halfEdgeIndex } = path;
      const floorPath = floorPaths[halfEdgeIndex];
      const { leftFloorAssembly, rightFloorAssembly } = floorPath;

      let edge: PolyLine2;
      let room: Room | undefined;
      let floorElevationOffset = 0;

      const priority = wainscotingPathPriority(leftRoom, rightRoom);

      if (priority === PathPriority.NONE) {
        return [amt, usedCloseDist];
      } else if (priority === PathPriority.LEFT) {
        edge = rightEdge;
        room = leftRoom;
        floorElevationOffset = floorAssemblyBaseOffset(leftFloorAssembly);
      } else if (priority === PathPriority.RIGHT) {
        edge = leftEdge;
        room = rightRoom;
        floorElevationOffset = floorAssemblyBaseOffset(rightFloorAssembly);
      } else {
        assertUnreachable(priority);
      }

      let roomAvailableHeight = availableHeight + floorElevationOffset;
      if (room && wainscotingSettings.porchWainscotingDatum === FoundationDatumVerticalAnchor.BOTTOM_OF_SLAB) {
        roomAvailableHeight -= slabDepthAtRoomClass(room.attributes.roomClass, foundationSystemSettings);
      }

      // add wainscoting
      [amt, usedCloseDist] = addWainscoting({
        wainscotingSettings,
        wainscotingFaces: faces,
        wainscotingTrim: trims,
        edge,
        startCorner,
        endCorner,
        prevAtFront,
        currAtFront,
        nextAtFront,
        currentU,
        startHeightOffset,
        availableHeight: roomAvailableHeight,
        addTopTrim,
        wainscotingContext,
        gaps: [],
        startHeight,
        addTopFace,
      });

      // exterior rooms sometimes have additional offsets on them
      // which we need to account for

      if (wall.start.isFreeEnd) {
        const cap = polyLine2New(wall.start.verts);
        addWainscoting({
          wainscotingSettings,
          wainscotingFaces: faces,
          wainscotingTrim: trims,
          edge: cap,
          startCorner,
          endCorner,
          prevAtFront,
          currAtFront,
          nextAtFront,
          currentU,
          startHeightOffset,
          availableHeight: roomAvailableHeight,
          addTopTrim,
          addTopFace,
          wainscotingContext,
          gaps: [],
          startHeight,
        });
      }

      if (wall.end.isFreeEnd) {
        const cap = polyLine2New(wall.end.verts);
        addWainscoting({
          wainscotingSettings,
          wainscotingFaces: faces,
          wainscotingTrim: trims,
          edge: cap,
          startCorner,
          endCorner,
          prevAtFront,
          currAtFront,
          nextAtFront,
          currentU,
          startHeightOffset,
          availableHeight: roomAvailableHeight,
          addTopTrim,
          addTopFace,
          wainscotingContext,
          gaps: [],
          startHeight,
        });
      }

      addWainscotingWallSegments({
        wainscotingSettings,
        halfEdgeIndex,
        edge,
        gaps: [],
        prevAtFront,
        currAtFront,
        nextAtFront,
        wainscotingContext,
        wallSegments: wallCuts,
      });

      addWainscotingWallSegments({
        wainscotingSettings,
        halfEdgeIndex,
        edge,
        gaps: [],
        prevAtFront,
        currAtFront,
        nextAtFront,
        wainscotingContext,
        wallSegments: wallBases,
      });
    }

    return [amt, usedCloseDist];
  };

  makeWainscoting({
    level,
    levels,
    perimeters: exteriorPerimeters,
    planarGraph,
    wainscotingSettings,
    wallSystem,
    facades,
    labels,
    mirror,
    wainscotingContext,
    allowWainscotingForEdge,
    makeWainscotingForEdge,
  });
}

interface WainscotingEdgeGeneratorProps {
  halfEdgeIndex: number;
  prevAtFront: boolean;
  currAtFront: boolean;
  nextAtFront: boolean;
  startCorner: WainscotingCorner;
  endCorner: WainscotingCorner;
  currentU: number;
}

interface WainscotingEdgeGeneratorOut {
  context: GenerateWainscotingContext;
  faces: Face[];
  trims: BRep[];
  wallCuts: WainscotingWallSegments[];
  wallBases: WainscotingWallSegments[];
}

type WainscotingEdgeGenerator = (
  props: WainscotingEdgeGeneratorProps,
  out: WainscotingEdgeGeneratorOut,
) => [number, boolean];

interface MakeWainscotingOptions {
  level: Level;
  levels: Building["levels"];
  perimeters: PlanarGraphLoops;
  planarGraph: PlanarGraph;
  wainscotingSettings: WainscotingSettings;
  wallSystem: WallSystem;
  facades: Table<Facade>;
  labels: HalfEdgeAssociationTable<Label>;
  mirror: boolean;
  wainscotingContext: GenerateWainscotingContext;
  allowWainscotingForEdge: (halfEdge: number) => boolean;
  makeWainscotingForEdge: WainscotingEdgeGenerator;
}

function makeWainscoting({
  level,
  levels,
  perimeters,
  planarGraph,
  wainscotingSettings,
  wallSystem,
  facades,
  labels,
  mirror,
  wainscotingContext,
  allowWainscotingForEdge,
  makeWainscotingForEdge,
}: MakeWainscotingOptions) {
  const {
    wainscotingMode,
    thickness,
    leftThreshold: wainscotingLeftThreshold,
    rightThreshold: wainscotingRightThreshold,
    lateralThreshold: wainscotingLateralThreshold,
  } = wainscotingSettings;

  assertIsNotUndefined(wainscotingSettings.viewNotes);

  const wainscotingViewEntityDescriptor = viewEntityDescriptorNew(
    uid(),
    ViewEntityType.WAINSCOTING,
    wainscotingSettings.viewNotes[WainscotingViewNoteType.WAINSCOTING],
  );

  const wainscotingCapViewEntityDescriptor = viewEntityDescriptorNew(
    uid(),
    ViewEntityType.WAINSCOTING_CAP,
    wainscotingSettings.viewNotes[WainscotingViewNoteType.CAP],
  );

  const forceWainscotingForEdge = (halfEdge: number): boolean => {
    if (wainscotingBehaviorForEdge(facades, labels, halfEdge) === FacadeTrimBehavior.ADD) {
      return true;
    }

    return false;
  };

  // iterate boundaries
  for (let i = 0, l = perimeters.faceVertices.length; i < l; i++) {
    const boundaryFaceHalfEdges = perimeters.faceHalfEdges[i];
    const boundaryFaceHalfEdgesCount = boundaryFaceHalfEdges.length;

    // calculate the U scale
    let perimeter = planarGraphVertexStripToPolyLine2(planarGraph, perimeters.faceVertices[i]);
    perimeter = polyLine2MergeCollinearEdges(perimeter);
    perimeter = polyLine2MergeCoincidentVertices(perimeter);

    // generate the front face information for the perimeter
    const maxLeftSideDist = wainscotingLeftThreshold || DEFAULT_WAINSCOTING_SIDE_THRESHOLD;
    const maxRightSideDist = wainscotingRightThreshold || DEFAULT_WAINSCOTING_SIDE_THRESHOLD;
    const maxLateralDist = wainscotingLateralThreshold || DEFAULT_WAINSCOTING_SIDE_THRESHOLD;

    const { leftClipPlane, rightClipPlane } = wainscotingContext;
    const { closeDist } = wainscotingSettings;

    let edgesFrontInfo = frontInfoNew();

    if (
      wainscotingMode === StreetOrientationMode.FRONT_FACES ||
      wainscotingMode === StreetOrientationMode.FRONT_AND_SIDE_FACES
    ) {
      edgesFrontInfo = getBuildingFrontInfo(
        planarGraph,
        boundaryFaceHalfEdges,
        0.0,
        -1.0,
        maxLeftSideDist,
        maxRightSideDist,
        leftClipPlane,
        rightClipPlane,
        mirror,
      );
    }

    if (wainscotingMode === StreetOrientationMode.FRONT_AND_SIDE_FACES) {
      edgesFrontInfo = getBuildingFrontInfo(
        planarGraph,
        boundaryFaceHalfEdges,
        -1.0,
        0.0,
        maxLateralDist,
        maxLateralDist,
        leftClipPlane,
        rightClipPlane,
        mirror,
        edgesFrontInfo.edgeAtFront,
      );

      edgesFrontInfo = getBuildingFrontInfo(
        planarGraph,
        boundaryFaceHalfEdges,
        1.0,
        0.0,
        maxLateralDist,
        maxLateralDist,
        leftClipPlane,
        rightClipPlane,
        mirror,
        edgesFrontInfo.edgeAtFront,
      );
    }

    const { edgeAtFront } = edgesFrontInfo;

    let acumU = 0.0;

    const faces: Face[] = [];
    const trims: BRep[] = [];
    const facePickHandles: (DocEntityHandle | undefined)[] = [];
    const trimPickHandles: (DocEntityHandle | undefined)[] = [];
    const faceReportingUnitHandles: (DocEntityHandle | undefined)[] = [];
    const trimReportingUnitHandles: (DocEntityHandle | undefined)[] = [];
    const wallCuts: WainscotingWallSegments[] = [];
    const wallBases: WainscotingWallSegments[] = [];
    const out = {
      context: wainscotingContext,
      faces,
      trims,
      wallCuts,
      wallBases,
    };

    // get the most furthest edge on the back of the house
    const startEdgeIndex = getFurthestEdgeIndex(planarGraph, boundaryFaceHalfEdges, 0.0, 1.0);

    const faceInfo: WallWainscotingFaces = {};
    // iterate the edges of the perimeter
    for (let j = boundaryFaceHalfEdgesCount - 1; j >= 0; j--) {
      const currJ = (j + startEdgeIndex) % boundaryFaceHalfEdgesCount;
      const prevJ = (currJ + 1) % boundaryFaceHalfEdgesCount;
      const nextJ = currJ > 0 ? currJ - 1 : boundaryFaceHalfEdgesCount - 1;

      const halfEdge = boundaryFaceHalfEdges[currJ];
      const nextHalfEdge = boundaryFaceHalfEdges[nextJ];
      const prevHalfEdge = boundaryFaceHalfEdges[prevJ];

      const currAllow = allowWainscotingForEdge(halfEdge);
      const prevAllow = allowWainscotingForEdge(prevHalfEdge);
      const nextAllow = allowWainscotingForEdge(nextHalfEdge);

      const nextAtFront = nextAllow && (edgeAtFront[nextHalfEdge] || false);
      const currAtFront = currAllow && (edgeAtFront[halfEdge] || forceWainscotingForEdge(halfEdge));
      const prevAtFront = prevAllow && (edgeAtFront[prevHalfEdge] || false);

      if (
        wainscotingMode !== StreetOrientationMode.ALL_FACES &&
        !currAtFront &&
        wainscotingSettings.closeDist <= DIST_EPSILON
      ) {
        continue;
      }

      // build edges

      const [edgeNormalX, edgeNormalY] = planarGraphHalfEdgeNormal(planarGraph, halfEdge);
      const [prevEdgeDirX, prevEdgeDirY] = planarGraphHalfEdgeTangent(planarGraph, prevHalfEdge);
      const [nextEdgeDirX, nextEdgeDirY] = planarGraphHalfEdgeTangent(planarGraph, nextHalfEdge);

      let startCorner = WainscotingCorner.NONE;
      let endCorner = WainscotingCorner.NONE;

      const startDot = vec2Dot(edgeNormalX, edgeNormalY, prevEdgeDirX, prevEdgeDirY);
      const endDot = vec2Dot(edgeNormalX, edgeNormalY, nextEdgeDirX, nextEdgeDirY);

      if (prevAllow && currAllow) {
        if (startDot >= DIST_EPSILON) {
          startCorner = WainscotingCorner.CONVEX;
        } else if (startDot <= -DIST_EPSILON) {
          startCorner = WainscotingCorner.CONCAVE;
        }
      }

      if (currAllow && nextAllow) {
        if (endDot <= -DIST_EPSILON) {
          endCorner = WainscotingCorner.CONVEX;
        } else if (endDot >= DIST_EPSILON) {
          endCorner = WainscotingCorner.CONCAVE;
        }
      }

      // get the wall of this half edge
      const wallId = wallSystem.halfEdgeToWall[halfEdge];
      const wall = wallSystem.walls[wallId];

      // if it is a valid wall
      if (currAllow && wall !== undefined) {
        const oldFaceLength = faces.length;
        const oldTrimLength = trims.length;

        const [amt, usedCloseDist] = makeWainscotingForEdge(
          {
            halfEdgeIndex: halfEdge,
            prevAtFront,
            currAtFront,
            nextAtFront,
            startCorner,
            endCorner,
            currentU: acumU,
          },
          out,
        );

        acumU += amt;

        const wallPath = getWallPath(wall);
        const instanceRoom = (() => {
          if (wallPath.leftRoom?.instanceId) {
            return wallPath.leftRoom;
          }

          if (wallPath.rightRoom?.instanceId) {
            return wallPath.rightRoom;
          }

          return undefined;
        })();

        const edgePickHandle = instanceRoom?.pickHandle;
        const edgeReportingUnitHandle = roomEntityHandleForSelectingReportingUnit(instanceRoom);

        for (let k = oldFaceLength; k < faces.length; k++) {
          facePickHandles.push(edgePickHandle);
          faceReportingUnitHandles.push(edgeReportingUnitHandle);
        }
        for (let k = oldTrimLength; k < trims.length; k++) {
          trimPickHandles.push(edgePickHandle);
          trimReportingUnitHandles.push(edgeReportingUnitHandle);
        }

        if (oldFaceLength !== faces.length && !usedCloseDist) {
          const info = {
            halfEdge: halfEdge,
            startFaceIndex: oldFaceLength,
            numFaces: faces.length - oldFaceLength,
          };
          if (faceInfo[wallId]) {
            faceInfo[wallId].push(info);
          } else {
            faceInfo[wallId] = [info];
          }
        }
      }
    }

    const wainscoting = wainscotingNew(
      level,
      faces,
      trims,
      thickness,
      faceInfo,
      wallCuts,
      wallBases,
      wainscotingViewEntityDescriptor,
      wainscotingCapViewEntityDescriptor,
      facePickHandles,
      trimPickHandles,
      faceReportingUnitHandles,
      trimReportingUnitHandles,
    );

    wainscotingContext.wainscoting[wainscoting.id] = wainscoting;

    if (level.id === bottomLevelId(levels)) {
      const { leftClipPlane, rightClipPlane } = edgesFrontInfo;
      wainscotingContext.leftClipPlane = leftClipPlane
        ? planeBSPMoveAlongNormal(leftClipPlane, -closeDist + thickness)
        : undefined;
      wainscotingContext.rightClipPlane = rightClipPlane
        ? planeBSPMoveAlongNormal(rightClipPlane, -closeDist + thickness)
        : undefined;
    }
  }
}

function wainscotingNew(
  level: Level,
  faces: Face[],
  trims: BRep[],
  thickness: number,
  faceInfo: WallWainscotingFaces,
  wallCuts: WainscotingWallSegments[],
  wallBases: WainscotingWallSegments[],
  wainscotingViewEntityDescriptor: ViewEntityDescriptor,
  wainscotingCapViewEntityDescriptor: ViewEntityDescriptor,
  facePickHandles?: (DocEntityHandle | undefined)[],
  trimPickHandles?: (DocEntityHandle | undefined)[],
  faceReportingUnitHandles?: (DocEntityHandle | undefined)[],
  trimReportingUnitHandles?: (DocEntityHandle | undefined)[],
): Wainscoting {
  return {
    ...buildingEntityBaseNew("WAINSCOTING", uid()),
    levelId: level.id,
    faces,
    trims,
    thickness,
    faceInfo,
    wallCuts,
    wallBases,
    wainscotingDescriptor: wainscotingViewEntityDescriptor,
    capDescriptor: wainscotingCapViewEntityDescriptor,
    facePickHandles,
    trimPickHandles,
    faceReportingUnitHandles,
    trimReportingUnitHandles,
  };
}

function clipWainscotingEdge(plane: PlaneBSP, edge: PolyLine2, start: boolean) {
  const intersection = intersectionLinePlaneBSP(plane, 0, edge[0], edge[1], 0, edge[2], edge[3], 0);
  if (intersection && intersection.t >= 0.0 && intersection.t <= 1.0) {
    const startIndex = start ? 0 : 2;
    edge[startIndex] = intersection.x;
    edge[startIndex + 1] = intersection.y;
  }
}

interface AddBasementWallWainscotingOptions {
  wainscotingSettings: WainscotingSettings;
  doorSettings: DoorSettings;
  wainscotingFaces: Face[];
  wainscotingTrim: BRep[];
  wallSystem: WallSystem;
  wall: Wall;
  wallAbove: SolidWall;
  startCorner: WainscotingCorner;
  endCorner: WainscotingCorner;
  prevAtFront: boolean;
  currAtFront: boolean;
  nextAtFront: boolean;
  currentU: number;
  startHeightOffset: number;
  availableHeight: number;
  addTopTrim: boolean;
  addTopFace: boolean;
  wainscotingContext: GenerateWainscotingContext;
  wallCuts: WainscotingWallSegments[];
  wallBases: WainscotingWallSegments[];
}

function addBasementWallWainscoting({
  wainscotingSettings,
  doorSettings,
  wainscotingFaces,
  wainscotingTrim,
  wallSystem,
  wall,
  wallAbove,
  startCorner,
  endCorner,
  prevAtFront,
  currAtFront,
  nextAtFront,
  currentU,
  startHeightOffset,
  availableHeight,
  addTopTrim,
  wainscotingContext,
  wallCuts,
  wallBases,
  addTopFace,
}: AddBasementWallWainscotingOptions): [number, boolean] {
  if (wall.wallType === WallType.SOLID_MASS) {
    return [currentU, false];
  }

  let leftSide = wallSideNew();
  let rightSide = wallSideNew();

  let mass: SolidWallMass | null = null;

  let startHeight = startHeightOffset;

  if (wall.wallType === WallType.SOLID) {
    leftSide = wall.leftSide;
    rightSide = wall.rightSide;
    mass = wall.mass;
    startHeight += wall.mass.floorElevation;
  } else {
    startHeight += wall.floorElevation;
  }

  const leftGarageDoors = wallSideNew({
    doors: leftSide.doors.filter(isGarageDoorOperationType),
  });
  const rightGarageDoors = wallSideNew({
    doors: rightSide.doors.filter(isGarageDoorOperationType),
  });

  // add wainscoting
  const path = getWallPath(wall);
  const { leftRoom, rightRoom, halfEdgeIndex } = path;

  const { leftRoom: aboveLeftRoom, rightRoom: aboveRightRoom } = getWallPath(wallAbove);

  let usedCloseDist = false;
  let acumU = currentU;

  const { leftEdge, rightEdge } = wallAbove?.mass ?? mass;

  const lenc = aboveLeftRoom?.attributes.roomEnclosure;
  const renc = aboveRightRoom?.attributes.roomEnclosure;

  const leftRoomWalled = lenc ? isWalledRoomEnclosure[lenc] : false;
  const rightRoomWalled = renc ? isWalledRoomEnclosure[renc] : false;

  if (!leftRoomWalled) {
    const isLeftSide = true;

    const edge = polyLine2Copy(leftEdge);
    const oppRoom = isLeftSide ? rightRoom : leftRoom;
    const oppRoomIsExteriorWalled = oppRoom && isExteriorRoomEnclosure[oppRoom.attributes.roomEnclosure];
    const oppRoomHasWainscoting = oppRoom && oppRoom.attributes.hasInteriorWallFacades;

    // get the windows and doors gaps
    const gaps = getWallGaps({
      leftEdge,
      rightEdge,
      leftSide,
      rightSide,
      isLeftSide,
      includeExteriorCasing: true,
      includeApronCasing: false,
      mergeGaps: true,
    });

    const [amt, used] = addWainscoting({
      wainscotingFaces,
      wainscotingTrim,
      wainscotingSettings,
      edge,
      gaps,
      startHeight,
      startCorner,
      endCorner,
      prevAtFront,
      currAtFront,
      nextAtFront,
      currentU: acumU,
      startHeightOffset,
      availableHeight,
      addTopTrim,
      addTopFace,
      wainscotingContext,
    });

    if (mass !== null && oppRoomIsExteriorWalled && oppRoomHasWainscoting) {
      wrapSolidWallWainscotingWallSegments({
        wallSystem,
        wainscotingSettings,
        doorSettings,
        mass,
        leftSide,
        rightSide,
        isLeftSide: isLeftSide,
        prevAtFront,
        currAtFront,
        nextAtFront,
        wainscotingContext,
        wallSegments: wallCuts,
        includeWallCuts: true,
      });

      wrapSolidWallWainscotingWallSegments({
        wallSystem,
        wainscotingSettings,
        doorSettings,
        mass,
        leftSide,
        rightSide,
        isLeftSide: isLeftSide,
        prevAtFront,
        currAtFront,
        nextAtFront,
        wainscotingContext,
        wallSegments: wallBases,
        includeWallCuts: false,
      });
    } else {
      addWainscotingWallSegments({
        wainscotingSettings,
        halfEdgeIndex,
        edge,
        gaps: gaps.filter((gap) => gap.bottom < wainscotingSettings.height),
        prevAtFront,
        currAtFront,
        nextAtFront,
        wainscotingContext,
        wallSegments: wallCuts,
      });

      // get the garage door gaps
      const wallGaps = getWallGaps({
        leftEdge,
        rightEdge,
        leftSide: leftGarageDoors,
        rightSide: rightGarageDoors,
        isLeftSide,
        mergeGaps: true,
        includeRoughOpeningAdjustment: true,
        getDoorRoughOpeningAdjustment: (door: GeneratedDoorPlacement): number => {
          return getDoorRoughOpeningAdjustment(door, doorSettings);
        },
      });

      addWainscotingWallSegments({
        wainscotingSettings,
        halfEdgeIndex,
        edge,
        gaps: wallGaps,
        prevAtFront,
        currAtFront,
        nextAtFront,
        wainscotingContext,
        wallSegments: wallBases,
      });
    }

    acumU += amt;
    usedCloseDist = used;
  }

  if (!rightRoomWalled) {
    const isLeftSide = false;

    const edge = polyLine2Copy(rightEdge);
    const oppRoom = isLeftSide ? rightRoom : leftRoom;
    const oppRoomIsExteriorWalled = oppRoom && isExteriorRoomEnclosure[oppRoom.attributes.roomEnclosure];
    const oppRoomHasWainscoting = oppRoom && oppRoom.attributes.hasInteriorWallFacades;

    // get the windows and doors gaps
    const gaps = getWallGaps({
      leftEdge,
      rightEdge,
      leftSide,
      rightSide,
      isLeftSide,
      includeExteriorCasing: true,
      includeApronCasing: false,
      mergeGaps: true,
    });

    const [amt, used] = addWainscoting({
      wainscotingFaces,
      wainscotingTrim,
      wainscotingSettings,
      edge,
      gaps,
      startHeight,
      startCorner,
      endCorner,
      prevAtFront,
      currAtFront,
      nextAtFront,
      currentU: acumU,
      startHeightOffset,
      availableHeight,
      addTopTrim,
      addTopFace,
      wainscotingContext,
    });

    if (oppRoomIsExteriorWalled && oppRoomHasWainscoting) {
      assertIsNotNullOrUndefined(mass);

      wrapSolidWallWainscotingWallSegments({
        wallSystem,
        wainscotingSettings,
        doorSettings,
        mass,
        leftSide,
        rightSide,
        isLeftSide,
        prevAtFront,
        currAtFront,
        nextAtFront,
        wainscotingContext,
        wallSegments: wallCuts,
        includeWallCuts: true,
      });

      wrapSolidWallWainscotingWallSegments({
        wallSystem,
        wainscotingSettings,
        doorSettings,
        mass,
        leftSide,
        rightSide,
        isLeftSide,
        prevAtFront,
        currAtFront,
        nextAtFront,
        wainscotingContext,
        wallSegments: wallBases,
        includeWallCuts: false,
      });
    } else {
      addWainscotingWallSegments({
        wainscotingSettings,
        halfEdgeIndex,
        edge,
        gaps: gaps.filter((gap) => gap.bottom < wainscotingSettings.height),
        prevAtFront,
        currAtFront,
        nextAtFront,
        wainscotingContext,
        wallSegments: wallCuts,
      });

      // get the garage door gaps
      const wallGaps = getWallGaps({
        leftEdge,
        rightEdge,
        leftSide: leftGarageDoors,
        rightSide: rightGarageDoors,
        isLeftSide,
        mergeGaps: true,
        includeRoughOpeningAdjustment: true,
        getDoorRoughOpeningAdjustment: (door: GeneratedDoorPlacement): number => {
          return getDoorRoughOpeningAdjustment(door, doorSettings);
        },
      });

      addWainscotingWallSegments({
        wainscotingSettings,
        halfEdgeIndex,
        edge,
        gaps: wallGaps,
        prevAtFront,
        currAtFront,
        nextAtFront,
        wainscotingContext,
        wallSegments: wallBases,
      });
    }

    acumU += amt;
    usedCloseDist = usedCloseDist || used;
  }

  return [acumU - currentU, usedCloseDist];
}

interface AddSolidWallWainscotingOptions {
  wainscotingSettings: WainscotingSettings;
  doorSettings: DoorSettings;
  wainscotingFaces: Face[];
  wainscotingTrim: BRep[];
  wallSystem: WallSystem;
  wall: SolidWall;
  startCorner: WainscotingCorner;
  endCorner: WainscotingCorner;
  prevAtFront: boolean;
  currAtFront: boolean;
  nextAtFront: boolean;
  currentU: number;
  startHeightOffset: number;
  availableHeight: number;
  addTopTrim: boolean;
  addTopFace: boolean;
  wainscotingContext: GenerateWainscotingContext;
  wallCuts: WainscotingWallSegments[];
  wallBases: WainscotingWallSegments[];
}

function addSolidWallWainscoting({
  wainscotingSettings,
  doorSettings,
  wainscotingFaces,
  wainscotingTrim,
  wallSystem,
  wall,
  startCorner,
  endCorner,
  prevAtFront,
  currAtFront,
  nextAtFront,
  currentU,
  startHeightOffset,
  availableHeight,
  addTopTrim,
  wainscotingContext,
  wallCuts,
  wallBases,
  addTopFace,
}: AddSolidWallWainscotingOptions): [number, boolean] {
  // add wainscoting
  const { mass, leftSide, rightSide } = wall;
  const path = getWallPath(wall);
  const { leftRoom, rightRoom, halfEdgeIndex } = path;

  const leftGarageDoors = wallSideNew({
    doors: leftSide.doors.filter(isGarageDoorOperationType),
  });
  const rightGarageDoors = wallSideNew({
    doors: rightSide.doors.filter(isGarageDoorOperationType),
  });

  let usedCloseDist = false;
  let acumU = currentU;

  const { leftEdge, rightEdge } = mass;
  const startHeight = mass.floorElevation + startHeightOffset;

  const leftEnc = leftRoom?.attributes.roomEnclosure;
  const rightEnc = rightRoom?.attributes.roomEnclosure;
  const leftRoomWalled = leftEnc ? isWalledRoomEnclosure[leftEnc] : undefined;
  const rightRoomWalled = rightEnc ? isWalledRoomEnclosure[rightEnc] : undefined;

  //the following boolean is used to compute the garage window bottom height in the coordindate system
  //of the wainscoting.
  const applyGarageWindowWainsotingFix: boolean = true;

  if (!leftRoomWalled) {
    const isLeftSide = true;

    const edge = polyLine2Copy(leftEdge);
    const oppRoom = isLeftSide ? rightRoom : leftRoom;
    const oppRoomIsExteriorWalled = oppRoom && isExteriorRoomEnclosure[oppRoom.attributes.roomEnclosure];
    const oppRoomHasWainscoting = oppRoom && oppRoom.attributes.hasInteriorWallFacades;

    // get the windows and doors gaps
    const gaps = getWallGaps({
      leftEdge,
      rightEdge,
      isLeftSide,
      leftSide,
      rightSide,
      includeExteriorCasing: true,
      includeApronCasing: false,
      mergeGaps: true,
      useWindowBottomZCalc: applyGarageWindowWainsotingFix,
    });

    const [amt, used] = addWainscoting({
      wainscotingFaces,
      wainscotingTrim,
      wainscotingSettings,
      edge,
      gaps,
      startHeight,
      startCorner,
      endCorner,
      prevAtFront,
      currAtFront,
      nextAtFront,
      currentU: acumU,
      startHeightOffset,
      availableHeight,
      addTopTrim,
      addTopFace,
      wainscotingContext,
    });

    if (oppRoomIsExteriorWalled && oppRoomHasWainscoting) {
      wrapSolidWallWainscotingWallSegments({
        wallSystem,
        wainscotingSettings,
        doorSettings,
        mass,
        leftSide,
        rightSide,
        isLeftSide: isLeftSide,
        prevAtFront,
        currAtFront,
        nextAtFront,
        wainscotingContext,
        wallSegments: wallCuts,
        includeWallCuts: true,
      });

      wrapSolidWallWainscotingWallSegments({
        wallSystem,
        wainscotingSettings,
        doorSettings,
        mass,
        leftSide,
        rightSide,
        isLeftSide: isLeftSide,
        prevAtFront,
        currAtFront,
        nextAtFront,
        wainscotingContext,
        wallSegments: wallBases,
        includeWallCuts: false,
      });
    } else {
      addWainscotingWallSegments({
        wainscotingSettings,
        halfEdgeIndex,
        edge,
        gaps: gaps.filter((gap) => gap.bottom < wainscotingSettings.height),
        prevAtFront,
        currAtFront,
        nextAtFront,
        wainscotingContext,
        wallSegments: wallCuts,
      });

      // get the garage door gaps
      const wallGaps = getWallGaps({
        leftEdge,
        rightEdge,
        leftSide: leftGarageDoors,
        rightSide: rightGarageDoors,
        isLeftSide,
        mergeGaps: true,
        includeRoughOpeningAdjustment: true,
        getDoorRoughOpeningAdjustment: (door: GeneratedDoorPlacement): number => {
          return getDoorRoughOpeningAdjustment(door, doorSettings);
        },
      });

      addWainscotingWallSegments({
        wainscotingSettings,
        halfEdgeIndex,
        edge,
        gaps: wallGaps,
        prevAtFront,
        currAtFront,
        nextAtFront,
        wainscotingContext,
        wallSegments: wallBases,
      });
    }

    acumU += amt;
    usedCloseDist = used;
  }

  if (!rightRoomWalled) {
    const isLeftSide = false;

    const edge = polyLine2Copy(rightEdge);
    const oppRoom = isLeftSide ? rightRoom : leftRoom;
    const oppRoomIsExteriorWalled = oppRoom && isExteriorRoomEnclosure[oppRoom.attributes.roomEnclosure];
    const oppRoomHasWainscoting = oppRoom && oppRoom.attributes.hasInteriorWallFacades;

    // get the windows and doors gaps
    const gaps = getWallGaps({
      leftEdge,
      rightEdge,
      leftSide,
      rightSide,
      isLeftSide,
      includeExteriorCasing: true,
      includeApronCasing: false,
      mergeGaps: true,
      useWindowBottomZCalc: applyGarageWindowWainsotingFix,
    });

    const [amt, used] = addWainscoting({
      wainscotingFaces,
      wainscotingTrim,
      wainscotingSettings,
      edge,
      gaps,
      startHeight,
      startCorner,
      endCorner,
      prevAtFront,
      currAtFront,
      nextAtFront,
      currentU: acumU,
      startHeightOffset,
      availableHeight,
      addTopTrim,
      addTopFace,
      wainscotingContext,
    });

    if (oppRoomIsExteriorWalled && oppRoomHasWainscoting) {
      wrapSolidWallWainscotingWallSegments({
        wallSystem,
        wainscotingSettings,
        doorSettings,
        mass,
        leftSide,
        rightSide,
        isLeftSide,
        prevAtFront,
        currAtFront,
        nextAtFront,
        wainscotingContext,
        wallSegments: wallCuts,
        includeWallCuts: true,
      });

      wrapSolidWallWainscotingWallSegments({
        wallSystem,
        wainscotingSettings,
        doorSettings,
        mass,
        leftSide,
        rightSide,
        isLeftSide,
        prevAtFront,
        currAtFront,
        nextAtFront,
        wainscotingContext,
        wallSegments: wallBases,
        includeWallCuts: false,
      });
    } else {
      addWainscotingWallSegments({
        wainscotingSettings,
        halfEdgeIndex,
        edge,
        gaps: gaps.filter((gap) => gap.bottom < wainscotingSettings.height),
        prevAtFront,
        currAtFront,
        nextAtFront,
        wainscotingContext,
        wallSegments: wallCuts,
      });

      // get the garage door gaps
      const wallGaps = getWallGaps({
        leftEdge,
        rightEdge,
        leftSide: leftGarageDoors,
        rightSide: rightGarageDoors,
        isLeftSide: isLeftSide,
        mergeGaps: true,
        includeRoughOpeningAdjustment: true,
        getDoorRoughOpeningAdjustment: (door: GeneratedDoorPlacement): number => {
          return getDoorRoughOpeningAdjustment(door, doorSettings);
        },
      });

      addWainscotingWallSegments({
        wainscotingSettings,
        halfEdgeIndex,
        edge,
        gaps: wallGaps,
        prevAtFront,
        currAtFront,
        nextAtFront,
        wainscotingContext,
        wallSegments: wallBases,
      });
    }

    acumU += amt;
    usedCloseDist = usedCloseDist || used;
  }

  return [acumU - currentU, usedCloseDist];
}

interface AddWainscotingWallSegmentsOptions {
  wainscotingSettings: WainscotingSettings;
  halfEdgeIndex: number;
  edge: PolyLine2;
  gaps: WallGap[];
  prevAtFront: boolean;
  currAtFront: boolean;
  nextAtFront: boolean;
  wainscotingContext: GenerateWainscotingContext;
  wallSegments: WainscotingWallSegments[];
}

/**
 * WainscotingWallBases represent the generators for displaying cross sections in plan
 */
function addWainscotingWallSegments({
  wainscotingSettings,
  halfEdgeIndex,
  edge,
  gaps,
  prevAtFront,
  currAtFront,
  nextAtFront,
  wainscotingContext,
  wallSegments,
}: AddWainscotingWallSegmentsOptions) {
  const { wainscotingMode } = wainscotingSettings;
  const { leftClipPlane, rightClipPlane } = wainscotingContext;

  const edgeLen = polyLine2EdgeLength(edge, 0);

  let gapRangeStart = 0.0;
  let gapRangeEnd = edgeLen;

  const isFilteringFaces = wainscotingMode !== StreetOrientationMode.ALL_FACES;
  const wallStartsAtFront = isFilteringFaces ? currAtFront || prevAtFront : true;
  const wallEndsAtFront = isFilteringFaces ? currAtFront || nextAtFront : true;

  // early return if it is a back wall of the building
  if (!wallStartsAtFront && !wallEndsAtFront) {
    return;
  }

  const wallSeg = wainscotingWallSegmentsNew(halfEdgeIndex);
  wallSegments.push(wallSeg);

  if (!wallStartsAtFront) {
    if (leftClipPlane) {
      clipWainscotingEdge(leftClipPlane, edge, true);
    } else {
      const amount = -Math.max(edgeLen, edgeLen * 0.5);
      polyLine2ExtendStart(edge, amount);
    }

    gapRangeStart = edgeLen - polyLine2EdgeLength(edge, 0);
  }

  if (!wallEndsAtFront) {
    if (rightClipPlane) {
      clipWainscotingEdge(rightClipPlane, edge, false);
    } else {
      const amount = -Math.max(edgeLen, edgeLen * 0.5);
      polyLine2ExtendEnd(edge, amount);
    }

    gapRangeEnd = polyLine2EdgeLength(edge, 0);
  }

  const addWallSegment = (segment: PolyLine2) => {
    const finalSegment = polyLine2Copy(segment);
    if (segment.findIndex((x) => isNaN(x)) === -1) {
      wallSeg.segments.push(finalSegment);
    }
  };

  let prevX = edge[0];
  let prevY = edge[1];

  // build the wall base using the gaps
  for (let i = 0, li = gaps.length; i < li; i++) {
    let { start, end } = gaps[i];

    if (start > gapRangeEnd || end < gapRangeStart) {
      continue;
    }

    start = Math.max(start, gapRangeStart);
    end = Math.min(end, gapRangeEnd);

    const [startX, startY] = polyLine2EdgeEval(edge, 0, start);
    const [endX, endY] = polyLine2EdgeEval(edge, 0, end);

    const prevEdge = polyLine2ByTwoPoints(prevX, prevY, startX, startY);

    addWallSegment(prevEdge);

    prevX = endX;
    prevY = endY;
  }

  // insert the last segment
  const newEdge = polyLine2New([prevX, prevY, edge[edge.length - 2], edge[edge.length - 1]]);

  addWallSegment(newEdge);
}

interface WrapSolidWallWainscotingWallSegmentOptions {
  wallSystem: WallSystem;
  wainscotingSettings: WainscotingSettings;
  doorSettings: DoorSettings;
  mass: SolidWallMass;
  leftSide: SolidWallSide;
  rightSide: SolidWallSide;
  isLeftSide: boolean;
  prevAtFront: boolean;
  currAtFront: boolean;
  nextAtFront: boolean;
  wainscotingContext: GenerateWainscotingContext;
  wallSegments: WainscotingWallSegments[];
  includeWallCuts: boolean;
}

function wrapSolidWallWainscotingWallSegments({
  wallSystem,
  wainscotingSettings,
  doorSettings,
  mass,
  leftSide,
  rightSide,
  isLeftSide,
  prevAtFront,
  currAtFront,
  nextAtFront,
  wainscotingContext,
  wallSegments,
  includeWallCuts,
}: WrapSolidWallWainscotingWallSegmentOptions) {
  const { wainscotingMode } = wainscotingSettings;
  const { leftClipPlane, rightClipPlane } = wainscotingContext;
  const { leftEdge, rightEdge, path } = mass;
  const { leftRoom, rightRoom, halfEdgeIndex } = path;
  const edge = polyLine2Copy(isLeftSide ? leftEdge : rightEdge);
  const oppEdge = polyLine2Copy(isLeftSide ? rightEdge : leftEdge);
  const interiorRoom = isLeftSide ? rightRoom : leftRoom;
  const edgeLen = polyLine2EdgeLength(edge, 0);
  const wallIndex = wallSystem.wallIndex;

  let gapRangeStart = 0.0;
  let gapRangeEnd = edgeLen;

  const isFilteringFaces = wainscotingMode !== StreetOrientationMode.ALL_FACES;
  const wallStartsAtFront = isFilteringFaces ? currAtFront || prevAtFront : true;
  const wallEndsAtFront = isFilteringFaces ? currAtFront || nextAtFront : true;

  // early return if it is a back wall of the building
  if (!wallStartsAtFront && !wallEndsAtFront) {
    return;
  }

  const wallSeg = wainscotingWallSegmentsNew(halfEdgeIndex);
  wallSegments.push(wallSeg);

  if (!wallStartsAtFront) {
    if (leftClipPlane) {
      clipWainscotingEdge(leftClipPlane, edge, true);
    } else {
      const amount = -Math.max(edgeLen, edgeLen * 0.5);
      polyLine2ExtendStart(edge, amount);
    }

    gapRangeStart = edgeLen - polyLine2EdgeLength(edge, 0);
  }

  if (!wallEndsAtFront) {
    if (rightClipPlane) {
      clipWainscotingEdge(rightClipPlane, edge, false);
    } else {
      const amount = -Math.max(edgeLen, edgeLen * 0.5);
      polyLine2ExtendEnd(edge, amount);
    }

    gapRangeEnd = polyLine2EdgeLength(edge, 0);
  }

  const addWallSegment = (segment: PolyLine2) => {
    const finalSegment = polyLine2Copy(segment);
    if (segment.findIndex((x) => isNaN(x)) === -1) {
      wallSeg.segments.push(finalSegment);
    }
  };

  // get the garage door gaps
  const gaps = getWallGaps({
    leftEdge,
    rightEdge,
    leftSide,
    rightSide,
    isLeftSide,
    mergeGaps: true,
    includeRoughOpeningAdjustment: true,
    getDoorRoughOpeningAdjustment: (door: GeneratedDoorPlacement): number => {
      return getDoorRoughOpeningAdjustment(door, doorSettings);
    },
  });

  let prevX = edge[0];
  let prevY = edge[1];

  // build the wall base using the gaps
  for (let i = 0, li = gaps.length; i < li; i++) {
    let { start, end } = gaps[i];

    if (start > gapRangeEnd || end < gapRangeStart) {
      continue;
    }

    start = Math.max(start, gapRangeStart);
    end = Math.min(end, gapRangeEnd);

    const [startX, startY] = polyLine2EdgeEval(edge, 0, start);
    const [endX, endY] = polyLine2EdgeEval(edge, 0, end);

    const prevEdge = polyLine2ByTwoPoints(prevX, prevY, startX, startY);

    addWallSegment(prevEdge);

    const interiorGaps = getWallGaps({
      leftEdge,
      rightEdge,
      leftSide,
      rightSide,
      isLeftSide: !isLeftSide,
      mergeGaps: true,
      includeRoughOpeningAdjustment: true,
      getDoorRoughOpeningAdjustment: (door: GeneratedDoorPlacement): number => {
        return getDoorRoughOpeningAdjustment(door, doorSettings);
      },
    });

    if (!(interiorGaps.length > 0)) {
      console.assert(false);
    }

    const { start: porticoStart, end: porticoEnd } = interiorGaps[0];

    const [porticoStartX, porticoStartY] = polyLine2EdgeEval(oppEdge, 0, porticoStart);
    const [porticoEndX, porticoEndY] = polyLine2EdgeEval(oppEdge, 0, porticoEnd);

    addWallSegment(polyLine2New([startX, startY, porticoEndX, porticoEndY]));
    addWallSegment(polyLine2New([porticoEndX, porticoEndY, oppEdge[2], oppEdge[3]]));

    const startEdgeIndex = wallIndex.planarGraph.halfEdgeIndexToPolygonEdgeIndex(halfEdgeIndex) + 1;

    assertIsNotUndefined(interiorRoom);

    for (let j = polyLine2EdgeCount(interiorRoom.outline) - 2; j >= 0; j--) {
      const wallStrip = wallIndex.wallStripFromRoomEdgeIndex(interiorRoom, startEdgeIndex + j);
      if (!wallStrip) {
        continue;
      }
      const porticoWalls = wallStrip.toWallArray();

      for (let k = porticoWalls.length - 1; k >= 0; k--) {
        const porticoWall = porticoWalls[k];
        if (porticoWall.wallType !== WallType.SOLID) {
          continue;
        }
        const { leftEdge: leftPorticoEdge, rightEdge: rightPorticoEdge } = solidWallSubstrateLayer(porticoWall);

        const porticoWallPath = getWallPath(porticoWall);
        assertIsNotUndefined(porticoWallPath.leftRoom);
        const interiorEdge = porticoWallPath.leftRoom.id === interiorRoom.id ? leftPorticoEdge : rightPorticoEdge;

        if (includeWallCuts) {
          const crossSectionMass = solidWallLayerWithFallback(porticoWall, WallLayerType.SUBSTRATE);
          let includeExteriorCasing: boolean | undefined;
          if (!wallMassCrossSectionLayer(crossSectionMass, WallLayerType.FINISH)) {
            includeExteriorCasing = false;
          }
          const { leftSide, rightSide } = porticoWall;
          const { leftOutlines, rightOutlines } = solidWallCrossSectionEdges({
            leftSide,
            rightSide,
            wallMass: crossSectionMass,
            includeExteriorCasing,
          });

          if (interiorEdge === leftPorticoEdge) {
            for (const outline of leftOutlines) {
              addWallSegment(outline);
            }
          } else {
            for (const outline of rightOutlines) {
              addWallSegment(outline);
            }
          }
        } else {
          addWallSegment(interiorEdge);
        }
      }
    }

    addWallSegment(polyLine2New([oppEdge[0], oppEdge[1], porticoStartX, porticoStartY]));
    addWallSegment(polyLine2New([porticoStartX, porticoStartY, endX, endY]));

    prevX = endX;
    prevY = endY;
  }

  // insert the last segment
  const newEdge = polyLine2New([prevX, prevY, edge[edge.length - 2], edge[edge.length - 1]]);

  addWallSegment(newEdge);
}

interface AddWainscotingOptions {
  wainscotingFaces: Face[];
  wainscotingTrim: BRep[];
  wainscotingSettings: WainscotingSettings;
  edge: PolyLine2;
  gaps: WallGap[];
  startCorner: WainscotingCorner;
  endCorner: WainscotingCorner;
  prevAtFront: boolean;
  currAtFront: boolean;
  nextAtFront: boolean;
  currentU: number;
  startHeight: number;
  startHeightOffset: number;
  availableHeight: number;
  addTopTrim: boolean;
  addTopFace: boolean;
  wainscotingContext: GenerateWainscotingContext;
}

function addWainscoting({
  wainscotingFaces,
  wainscotingTrim,
  wainscotingSettings,
  edge,
  gaps,
  startCorner,
  endCorner,
  prevAtFront,
  currAtFront,
  nextAtFront,
  startHeight,
  currentU,
  startHeightOffset,
  availableHeight,
  addTopTrim,
  addTopFace,
  wainscotingContext,
}: AddWainscotingOptions): [number, boolean] {
  const addWainscotingFace = (edge: PolyLine2, startU: number, bottom: number, height: number, offset: number) => {
    if (Math.abs(height) < DIST_EPSILON) {
      return;
    }

    const basePlane = planeNewXY(0, 0, bottom);
    const newFace = extrudeLine2(basePlane, edge[0], edge[1], edge[2], edge[3], 0, 0, height);

    // could fail when, for example, the edge is length zero
    if (!newFace) {
      return;
    }

    // adjust the face plane to do a proper texture tiling
    planeMove(newFace.plane, -newFace.plane.uX * startU, -newFace.plane.uY * startU, -newFace.plane.uZ * startU);
    planeMove(newFace.plane, -newFace.plane.vX * bottom, -newFace.plane.vY * bottom, -newFace.plane.vZ * bottom);
    polyLine2Move(newFace.loops[0], startU, bottom);

    const [nX, nY, nZ] = planeNormal(newFace.plane);
    faceOrNullMove(newFace, nX * offset, nY * offset, nZ * offset);
    wainscotingFaces.push(newFace);
  };

  const WAINSCOTING_TRIM_HEIGHT = wainscotingSettings.capHeight;
  const WAINSCOTING_TRIM_EXTRA_THICKNESS = wainscotingSettings.capThickness;

  const addWainscotingTrim = (
    edge: PolyLine2,
    height: number,
    thickness: number,
    extendStart: boolean,
    extendEnd: boolean,
  ) => {
    const basePlane = planeNewXY(0, 0, height);
    const rail = polyLine3OnPlane(edge, basePlane);

    if (thickness < DIST_EPSILON) {
      return;
    }

    if (extendStart) {
      polyLine3ExtendStart(rail, WAINSCOTING_TRIM_EXTRA_THICKNESS);
    }

    if (extendEnd) {
      polyLine3ExtendEnd(rail, WAINSCOTING_TRIM_EXTRA_THICKNESS);
    }

    const trimDepth = thickness + WAINSCOTING_TRIM_EXTRA_THICKNESS;
    const profile = polyLine2New([
      0,
      -WAINSCOTING_TRIM_HEIGHT,
      0,
      0,
      trimDepth,
      0,
      trimDepth,
      -WAINSCOTING_TRIM_HEIGHT,
      0,
      -WAINSCOTING_TRIM_HEIGHT,
    ]);

    wainscotingTrim.push(extrudeFaceAlongPolyLine3([profile], rail, 0, 0, 1));
  };

  const addWainscotingTopFace = (edge: PolyLine2, startU: number, height: number, thickness: number) => {
    if (thickness < DIST_EPSILON) {
      return;
    }

    const plane = planeNewXY(0, 0, height);
    const [edgeNX, edgeNY] = polyLine2EdgeNormal(edge, 0);

    const offsetX = edgeNX * thickness;
    const offsetY = edgeNY * thickness;

    const newFace = extrudeLine2(
      plane,
      edge[0] + offsetX,
      edge[1] + offsetY,
      edge[2] + offsetX,
      edge[3] + offsetY,
      -offsetX,
      -offsetY,
      0,
    );

    if (!newFace) {
      return;
    }

    // adjust the face plane to do a proper texture tiling
    planeMove(newFace.plane, -newFace.plane.uX * startU, -newFace.plane.uY * startU, -newFace.plane.uZ * startU);
    planeMove(newFace.plane, -newFace.plane.vX * height, -newFace.plane.vY * height, -newFace.plane.vZ * height);
    polyLine2Move(newFace.loops[0], startU, height);

    wainscotingFaces.push(newFace);
  };

  const addWainscotingSideFace = (
    edge: PolyLine2,
    isStart: boolean,
    isGap: boolean,
    bottom: number,
    height: number,
    currentU: number,
  ) => {
    if (Math.abs(height) < DIST_EPSILON) {
      return;
    }

    const [nX, nY] = polyLine2EdgeNormal(edge, 0);

    const x0 = edge[isStart ? 0 : edge.length - 2];
    const y0 = edge[isStart ? 1 : edge.length - 1];

    const x1 = x0 + nX * wainscotingThickness;
    const y1 = y0 + nY * wainscotingThickness;

    let sideEdge = polyLine2ByTwoPoints(x0, y0, x1, y1);

    if (isStart === isGap) {
      sideEdge = polyLine2Reverse(sideEdge);
    }

    addWainscotingFace(sideEdge, currentU, bottom, height, 0);
  };

  const { leftClipPlane, rightClipPlane } = wainscotingContext;
  const {
    wainscotingMode,
    thickness: wainscotingThickness,
    removeWainscotingSegmentsUnderLength,
  } = wainscotingSettings;

  const wainscotingHeight = availableHeight - startHeightOffset;

  let finalEdgeLen = polyLine2EdgeLength(edge, 0);

  if (startCorner === WainscotingCorner.CONVEX) {
    finalEdgeLen += wainscotingThickness;
  } else if (startCorner === WainscotingCorner.CONCAVE) {
    finalEdgeLen -= wainscotingThickness;
  }

  if (endCorner === WainscotingCorner.CONVEX) {
    finalEdgeLen += wainscotingThickness;
  } else if (endCorner === WainscotingCorner.CONCAVE) {
    finalEdgeLen -= wainscotingThickness;
  }

  const isFilteringFaces = wainscotingMode !== StreetOrientationMode.ALL_FACES;
  const wallStartsAtFront = isFilteringFaces ? currAtFront || prevAtFront : true;
  const wallEndsAtFront = isFilteringFaces ? currAtFront || nextAtFront : true;

  // early return if it is a back wall of the building
  if (!wallStartsAtFront && !wallEndsAtFront) {
    return [finalEdgeLen, false];
  }

  const extendedEdge = polyLine2Copy(edge);
  const edgeLen = polyLine2EdgeLength(extendedEdge, 0);
  let gapRangeStart = 0.0;
  let gapRangeEnd = edgeLen;

  // This is how much the wainscoting will wrap around if front only is enabled.
  const closeDist = wainscotingSettings.closeDist;
  let usedCloseDist = false;

  // clip the edge if needed (for the wainscoting only on the front of the building)
  if (!wallStartsAtFront) {
    if (leftClipPlane) {
      clipWainscotingEdge(leftClipPlane, extendedEdge, true);
    } else {
      const amount = -Math.max(edgeLen - closeDist, edgeLen * 0.5);
      usedCloseDist = usedCloseDist || amount === -(edgeLen - closeDist);
      polyLine2ExtendStart(extendedEdge, amount);
    }

    gapRangeStart = edgeLen - polyLine2EdgeLength(extendedEdge, 0);

    currentU += gapRangeStart;
  }

  if (!wallStartsAtFront || !prevAtFront) {
    addWainscotingSideFace(extendedEdge, true, false, startHeight, wainscotingHeight, currentU - wainscotingThickness);
  }

  if (!wallEndsAtFront) {
    if (rightClipPlane) {
      clipWainscotingEdge(rightClipPlane, extendedEdge, false);
    } else {
      const amount = -Math.max(edgeLen - closeDist, edgeLen * 0.5);
      usedCloseDist = usedCloseDist || amount === -(edgeLen - closeDist);
      polyLine2ExtendEnd(extendedEdge, amount);
    }

    gapRangeEnd = polyLine2EdgeLength(extendedEdge, 0);
  }

  if (!wallEndsAtFront || !nextAtFront) {
    addWainscotingSideFace(extendedEdge, false, false, startHeight, wainscotingHeight, currentU);
  }

  const extendAllCorners = !isFilteringFaces || closeDist > DIST_EPSILON;

  // extend/contract for corners
  let startOffset = 0;
  if (wallStartsAtFront && (prevAtFront || extendAllCorners)) {
    if (startCorner === WainscotingCorner.CONVEX) {
      startOffset = wainscotingThickness;
    } else if (startCorner === WainscotingCorner.CONCAVE) {
      startOffset = -wainscotingThickness;
    }
  }

  let endOffset = 0;
  if (wallEndsAtFront && (nextAtFront || extendAllCorners)) {
    if (endCorner === WainscotingCorner.CONVEX) {
      endOffset = wainscotingThickness;
    } else if (endCorner === WainscotingCorner.CONCAVE) {
      endOffset = -wainscotingThickness;
    }
  }

  polyLine2ExtendStart(extendedEdge, startOffset);
  polyLine2ExtendEnd(extendedEdge, endOffset);

  const lastGapId = gaps.length - 1;
  const addFirstSegment = gaps.length === 0 || gaps[0].start > DIST_EPSILON;
  const addLastSegment = gaps.length === 0 || gaps[lastGapId].end < edgeLen - DIST_EPSILON;

  let prevX = extendedEdge[0];
  let prevY = extendedEdge[1];

  const gapOffset = 0.1;

  // build the wainscoting using the gaps
  let isPrevDoorGap = false;
  for (let i = 0, li = gaps.length; i < li; i++) {
    const gap = gaps[i];
    const { height, source } = gap;
    let { start, end, bottom } = gap;
    bottom -= startHeightOffset;

    if (start > gapRangeEnd || end < gapRangeStart) {
      continue;
    }

    start = Math.max(start, gapRangeStart);
    end = Math.min(end, gapRangeEnd);

    const [startX, startY] = polyLine2EdgeEval(edge, 0, start - gapOffset);
    const [endX, endY] = polyLine2EdgeEval(edge, 0, end + gapOffset);

    const prevEdge = polyLine2ByTwoPoints(prevX, prevY, startX, startY);
    const nextEdge = polyLine2ByTwoPoints(startX, startY, endX, endY);

    const isNextDoorGap = gap.bottom < DIST_EPSILON;

    const isPrevEdgeInvalid =
      removeWainscotingSegmentsUnderLength !== -1 &&
      polyLine2Length(prevEdge) < removeWainscotingSegmentsUnderLength &&
      (isPrevDoorGap || isNextDoorGap);

    const removePrevEdge = isPrevEdgeInvalid || (i === 0 && !addFirstSegment);
    if (!removePrevEdge) {
      addWainscotingFace(prevEdge, currentU, startHeight, wainscotingHeight, wainscotingThickness);

      if (addTopTrim) {
        addWainscotingTrim(
          prevEdge,
          startHeight + wainscotingHeight,
          wainscotingThickness,
          startCorner === WainscotingCorner.CONVEX && i === 0 && (prevAtFront || closeDist > DIST_EPSILON),
          false,
        );
      } else if (addTopFace) {
        addWainscotingTopFace(prevEdge, currentU, startHeight + wainscotingHeight, wainscotingThickness);
      }
    }

    currentU += polyLine2EdgeLength(prevEdge, 0);

    if (
      source === WallGapSource.WINDOW &&
      bottom > startHeightOffset - DIST_EPSILON &&
      bottom > WINDOW_BOTTOM_WAINSCOTING_THRESHOLD
    ) {
      addWainscotingFace(nextEdge, currentU, startHeight, Math.min(wainscotingHeight, bottom), wainscotingThickness);
    } else if (bottom > -startHeightOffset - DIST_EPSILON && bottom > DOOR_BOTTOM_WAINSCOTING_THRESHOLD) {
      addWainscotingFace(nextEdge, currentU, startHeight, Math.min(wainscotingHeight, bottom), wainscotingThickness);
    }

    if (wainscotingHeight > bottom + height) {
      addWainscotingFace(
        nextEdge,
        currentU,
        startHeight + bottom + height,
        wainscotingHeight - height - bottom,
        wainscotingThickness,
      );
      if (addTopTrim) {
        addWainscotingTrim(nextEdge, startHeight + wainscotingHeight, wainscotingThickness, false, false);
      } else if (addTopFace) {
        addWainscotingTopFace(nextEdge, currentU, startHeight + wainscotingHeight, wainscotingThickness);
      }
    }

    if (wainscotingHeight >= bottom && !isPrevEdgeInvalid) {
      if (i !== 0 || addFirstSegment) {
        addWainscotingSideFace(
          nextEdge,
          true,
          true,
          startHeight + bottom,
          Math.min(wainscotingHeight - bottom, height + WAINSCOTING_TRIM_HEIGHT),
          currentU,
        );
      }

      if (i !== lastGapId || addLastSegment) {
        addWainscotingSideFace(
          nextEdge,
          false,
          true,
          startHeight + bottom,
          Math.min(wainscotingHeight - bottom, height + WAINSCOTING_TRIM_HEIGHT),
          currentU,
        );
      }
    }

    if (availableHeight < bottom || availableHeight > bottom + height) {
      if (addTopTrim && wainscotingHeight < bottom + 1) {
        if (addTopTrim && gap.end - gap.start < 120) {
          addWainscotingTrim(
            nextEdge,
            startHeight + Math.min(wainscotingHeight, bottom),
            wainscotingThickness,
            false,
            false,
          );
        }
      } else if (addTopFace) {
        addWainscotingTopFace(
          nextEdge,
          currentU,
          startHeight + Math.min(wainscotingHeight, bottom),
          wainscotingThickness,
        );
      }
    }

    currentU += polyLine2EdgeLength(nextEdge, 0);

    prevX = endX;
    prevY = endY;

    isPrevDoorGap = isNextDoorGap;
  }

  // insert the last wainscoting segment
  if (addLastSegment) {
    const newEdge = polyLine2New([
      prevX,
      prevY,
      extendedEdge[extendedEdge.length - 2],
      extendedEdge[extendedEdge.length - 1],
    ]);

    addWainscotingFace(newEdge, currentU, startHeight, wainscotingHeight, wainscotingThickness);

    if (addTopTrim) {
      addWainscotingTrim(
        newEdge,
        startHeight + wainscotingHeight,
        wainscotingThickness,
        false,
        endCorner === WainscotingCorner.CONVEX && (nextAtFront || closeDist > DIST_EPSILON),
      );
    } else if (addTopFace) {
      addWainscotingTopFace(newEdge, currentU, startHeight + wainscotingHeight, wainscotingThickness);
    }
  }

  return [finalEdgeLen, usedCloseDist];
}

function getBuildingFrontInfo(
  planarGraph: PlanarGraph,
  faceHalfEdges: number[],
  buildingNormalX: number = 0.0,
  buildingNormalY: number = -1.0,
  maxLeftSideDist: number = -1.0,
  maxRightSideDist: number = -1.0,
  leftClipPlane: PlaneBSP | undefined,
  rightClipPlane: PlaneBSP | undefined,
  mirror: boolean,
  edges: boolean[] = [],
): FrontInfo {
  const boundaryFaceHalfEdgesCount = faceHalfEdges.length;

  let newLeftClipPlane: PlaneBSP | null = null;
  let newRightClipPlane: PlaneBSP | null = null;

  // look for the start edge (the closest front wall)
  const startEdgeIndex = getFurthestEdgeIndex(planarGraph, faceHalfEdges, buildingNormalX, buildingNormalY, mirror);

  const buildClipPlane = (halfEdge: number, forward: boolean): PlaneBSP => {
    const [x, y] = forward
      ? planarGraphHalfEdgeStart(planarGraph, halfEdge)
      : planarGraphHalfEdgeEnd(planarGraph, halfEdge);
    const [edgeTangentX, edgeTangentY] = planarGraphHalfEdgeNormal(planarGraph, halfEdge, true);

    return planeBSPByPointAndNormal(x, y, 0, edgeTangentX, edgeTangentY, 0);
  };

  const addBuildingFrontInfo = (halfEdge: number, maxSideDist: number, clipPlane: PlaneBSP | undefined): boolean => {
    const [edgeNormalX, edgeNormalY] = planarGraphHalfEdgeNormal(planarGraph, halfEdge, true);
    const edgeLength = planarGraphHalfEdgeLength(planarGraph, halfEdge);
    const dot = vec2Dot(buildingNormalX, buildingNormalY, edgeNormalX, edgeNormalY);

    const [x1, y1] = planarGraphHalfEdgeStart(planarGraph, halfEdge);
    const [x2, y2] = planarGraphHalfEdgeEnd(planarGraph, halfEdge);

    const side1 = clipPlane ? vec3PlaneBSPSide(x1, y1, 0, clipPlane) : PlaneSide.BACK;
    const side2 = clipPlane ? vec3PlaneBSPSide(x2, y2, 0, clipPlane) : PlaneSide.BACK;

    const forceFront =
      (side1 === PlaneSide.COPLANAR || side1 === PlaneSide.FRONT) &&
      (side2 === PlaneSide.COPLANAR || side2 === PlaneSide.FRONT);

    const forceBack = clipPlane && side1 === PlaneSide.BACK && side2 === PlaneSide.BACK;

    // if it is a back face we stop
    if (forceBack || dot <= -DIST_EPSILON) {
      return false;
    } else if (dot >= DIST_EPSILON || forceFront) {
      sideDist = 0.0;
      for (let j = 0, lj = prevHalfEdges.length; j < lj; j++) {
        edges[prevHalfEdges[j]] = true;
      }
      prevHalfEdges = [];
      edges[halfEdge] = true;
      lastFrontEdge = halfEdge;
    } else {
      sideDist += edgeLength;
      prevHalfEdges.push(halfEdge);

      if (maxSideDist >= 0.0 && sideDist > maxSideDist) {
        return false;
      }
    }

    return true;
  };

  // travel forward
  let prevHalfEdges: number[] = [];
  let sideDist = 0.0;
  let lastFrontEdge = -1;
  for (let i = 0; i < boundaryFaceHalfEdgesCount; i++) {
    const halfEdge = faceHalfEdges[(startEdgeIndex + i) % boundaryFaceHalfEdgesCount];
    if (!addBuildingFrontInfo(halfEdge, mirror ? maxRightSideDist : maxLeftSideDist, leftClipPlane)) {
      if (lastFrontEdge >= 0) {
        newLeftClipPlane = buildClipPlane(lastFrontEdge, true);
      }
      break;
    }
  }

  // travel backwards
  prevHalfEdges = [];
  sideDist = 0.0;
  lastFrontEdge = -1;
  for (let i = boundaryFaceHalfEdgesCount - 1; i >= 0; i--) {
    const halfEdge = faceHalfEdges[(startEdgeIndex + i + 1) % boundaryFaceHalfEdgesCount];
    if (!addBuildingFrontInfo(halfEdge, mirror ? maxLeftSideDist : maxRightSideDist, rightClipPlane)) {
      if (lastFrontEdge >= 0) {
        newRightClipPlane = buildClipPlane(lastFrontEdge, false);
      }
      break;
    }
  }

  return frontInfoNew(edges, newLeftClipPlane, newRightClipPlane);
}

function allowWainscotingForRoom(blocks: Table<Block>, room: Room | undefined): boolean {
  if (!room) {
    return true;
  }

  const block = blocks[room.parentId];

  if (!room) {
    return true;
  }

  return !block.suppressWainscoting;
}

export function makeWainscotingCrossSections(
  wainscoting: Building["wainscoting"],
  levelId: string,
  allWallCuts: boolean,
): PolyLine2[] {
  const wainscotingSections: PolyLine2[] = [];

  for (const wainsId in wainscoting) {
    const levelWainscoting = wainscoting[wainsId];

    if (levelWainscoting.levelId !== levelId) {
      continue;
    }

    const { thickness, wallCuts, wallBases } = levelWainscoting;

    const wallSegments = allWallCuts ? wallCuts : wallBases;
    const wainscotingThickness = thickness || 3.625;

    const segments: PolyLine2[] = [];
    for (const wallSeg of wallSegments) {
      segments.push(...wallSeg.segments);
    }

    makePerimeterCrossSections(segments, wainscotingThickness, wainscotingSections);
  }

  return wainscotingSections;
}

function makePerimeterCrossSections(segments: PolyLine2[], thickness: number, sections: PolyLine2[] = []): PolyLine2[] {
  const joinedSegments = joinPolyLine2Sequences(segments);

  for (const joinedSegment of joinedSegments) {
    const plb = polyLine2Reverse(offsetPolyLine2(joinedSegment, thickness));
    if (polyLine2IsClosed(joinedSegment)) {
      sections.push(joinedSegment);
      sections.push(plb);
    } else {
      const section = polyLine2Concat(joinedSegment, plb, true);
      sections.push(section);
    }
  }

  return sections;
}

export function makeWainscotingDisjointCrossSections(
  wainscoting: Building["wainscoting"],
  levelId: string,
  allWallCuts: boolean,
) {
  const crossSections: PolyLine2[] = [];

  for (const wainsId in wainscoting) {
    const levelWainscoting = wainscoting[wainsId];
    if (levelWainscoting.levelId !== levelId) {
      continue;
    }

    const { thickness, wallBases, wallCuts } = levelWainscoting;

    const wallSegments = allWallCuts ? wallCuts : wallBases;
    const wainscotingThickness = thickness || 3.625;

    const segments: PolyLine2[] = [];
    const segmentWallBases: WainscotingWallSegments[] = [];

    for (const wallSeg of wallSegments) {
      segments.push(...wallSeg.segments);
      segmentWallBases.push(...wallSeg.segments.map(() => wallSeg));
    }

    // join wall bases into polyline sequences and offset them,
    // then find the appropriate half edge for each offset on the sequence
    const joinedSegments = joinPolyLine2Sequences(segments, undefined, false);

    for (const joinedSegment of joinedSegments) {
      const ps = polyLine2Copy(joinedSegment);
      const psEdgeCount = polyLine2EdgeCount(ps);
      const plb = offsetPolyLine2(ps, wainscotingThickness);

      for (let i = 0; i < psEdgeCount; i++) {
        const edge = polyLine2Edge(ps, i);
        const [ex, ey] = polyLine2EdgeEval(edge, 0, 0.5, true);

        const edgeIndex = segments.findIndex((s) => {
          const [sx, sy] = polyLine2EdgeEval(s, 0, 0.5, true);
          return vec2DistSquared(ex, ey, sx, sy) <= DIST_EPSILON_SQUARED;
        });

        if (edgeIndex < 0) {
          continue;
        }

        const edgePerimeter = polyLine2Reverse(polyLine2Edge(plb, i));
        const planProfile = polyLine2Concat(edge, edgePerimeter, true);

        crossSections.push(planProfile);
      }
    }
  }
  return crossSections;
}
